// ============================================================================
// One checklist row, and the modal that hands it to somebody else.
//
// ⛔ THIS COMPONENT IS RENDERED BY BOTH VIEWS. The consolidated tab and the
// "My items" tab pass the same ChecklistItem to the same component, so the
// wording, the sub-steps and the due date cannot drift between them. The only
// difference either view is allowed to make is WHICH items it passes in.
//
// An automatic item is shown, never hidden: a scheduled date instead of a
// checkbox, and — for app access — a Revoke now button, so the COO can see
// what the app is about to do and go early if they want to.
// ============================================================================

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  Clock,
  RefreshCw,
  ShieldOff,
} from 'lucide-react';
import { Button, Card, useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { OWNER_LABELS, type ChecklistItem, type SubStep } from './types';
import {
  assignItem,
  completeItem,
  revokeAccessNow,
  setSubSteps,
  useEmployeePicker,
} from './hooks/use-separation-data';

function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  // ⛔ A bare DATE is rendered in UTC. Parsing '2026-09-12' as local time shows
  // the day BEFORE for anyone west of Greenwich.
  const dt = new Date(d + 'T00:00:00Z');
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export interface ChecklistItemRowProps {
  item: ChecklistItem;
  names: Map<string, string>;
  /** Whoever is looking. Used only to label, never to decide what is returned. */
  canAct: boolean;
  onChanged: () => void;
  /** Set on the work-handoff items so the assignment screen can be opened. */
  onOpenHandoff?: (item: ChecklistItem) => void;
}

export function ChecklistItemRow({
  item,
  names,
  canAct,
  onChanged,
  onOpenHandoff,
}: ChecklistItemRowProps) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [odometer, setOdometer] = useState('');
  const [askOdometer, setAskOdometer] = useState(false);

  const done = !!item.completed_at;
  const ownerLabel = OWNER_LABELS[item.owner_position_key] ?? item.owner_position_key;
  const assignee = item.assigned_to_eid ? names.get(item.assigned_to_eid) : null;
  const isHandoff =
    item.item_key === 'facilities_reassigned' ||
    item.item_key === 'route_stops_reassigned' ||
    item.item_key === 'pipeline_cards_reassigned';

  const subSteps = useMemo<SubStep[]>(
    () => (Array.isArray(item.sub_steps) ? item.sub_steps : []),
    [item.sub_steps],
  );
  const plainSteps = subSteps.filter((s) => !s.ref && s.label);

  async function tick() {
    if (item.item_key === 'vehicle_returned' && !askOdometer) {
      setAskOdometer(true);
      return;
    }
    setBusy(true);
    try {
      await completeItem(
        item.item_id,
        null,
        item.item_key === 'vehicle_returned' ? Number(odometer) : null,
      );
      toast.success('Done', { description: item.label });
      setAskOdometer(false);
      onChanged();
    } catch (err) {
      toast.error("Couldn't tick that", { description: errorMessage(err, 'Try again.') });
    } finally {
      setBusy(false);
    }
  }

  async function toggleSubStep(idx: number) {
    const next = subSteps.map((s, i) => (i === idx ? { ...s, done: !s.done } : s));
    setBusy(true);
    try {
      await setSubSteps(item.item_id, next);
      onChanged();
    } catch (err) {
      toast.error("Couldn't save that step", {
        description: errorMessage(err, 'Try again.'),
      });
    } finally {
      setBusy(false);
    }
  }

  async function revokeNow() {
    setBusy(true);
    try {
      await revokeAccessNow(item.separation_id);
      toast.success('App access removed now');
      onChanged();
    } catch (err) {
      toast.error("Couldn't revoke access", {
        description: errorMessage(err, 'Try again.'),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      className={`p-4 ${item.is_blocking && !done ? 'border-sentiment-critical-text/40' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {item.is_automatic ? (
            <RefreshCw
              className="h-4 w-4 text-tcs-text-secondary"
              aria-label="The app does this one"
            />
          ) : (
            <button
              type="button"
              disabled={!canAct || busy || done}
              onClick={() => void tick()}
              aria-label={done ? 'Done' : `Mark done: ${item.label}`}
              className={`flex h-5 w-5 items-center justify-center rounded border ${
                done
                  ? 'border-sentiment-positive-text bg-sentiment-positive-fill'
                  : 'border-tcs-border'
              } ${!canAct || done ? '' : 'hover:border-tcs-text'}`}
            >
              {done && <Check className="h-3.5 w-3.5 text-sentiment-positive-text" />}
            </button>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {item.is_blocking && !done && (
              <span className="inline-flex items-center gap-1 rounded bg-sentiment-critical-fill px-1.5 py-0.5 text-xs font-medium text-sentiment-critical-text">
                <AlertTriangle className="h-3 w-3" /> Blocking
              </span>
            )}
            <span
              className={`text-sm font-medium ${done ? 'text-tcs-text-secondary line-through' : 'text-tcs-text'}`}
            >
              {item.label}
            </span>
          </div>

          {item.detail && (
            <p className="mt-1 text-sm text-tcs-text-secondary">{item.detail}</p>
          )}

          {/* Why this item exists at all — the live query that found it. */}
          {item.discovered && (
            <p className="mt-1 text-xs italic text-tcs-text-muted">{item.discovered}</p>
          )}

          {item.escalated_reason && (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-sentiment-warning-text">
              <ShieldOff className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                {item.escalated_reason} — that group is assigned to the COO rather than to
                the person walking out.
              </span>
            </p>
          )}

          {plainSteps.length > 0 && (
            <ol className="mt-2 space-y-1.5">
              {subSteps.map((s, i) =>
                s.ref || !s.label ? null : (
                  <li key={`${item.item_id}-${i}`} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!s.done}
                      disabled={!canAct || busy || done}
                      onChange={() => void toggleSubStep(i)}
                      className="mt-0.5 h-3.5 w-3.5"
                      aria-label={s.label}
                    />
                    <span
                      className={`text-xs ${s.done ? 'text-tcs-text-muted line-through' : 'text-tcs-text-secondary'}`}
                    >
                      {s.label}
                    </span>
                  </li>
                ),
              )}
            </ol>
          )}

          {askOdometer && (
            <div className="mt-2 flex flex-wrap items-end gap-2 rounded border border-tcs-border p-2">
              <label className="text-xs text-tcs-text-secondary">
                Odometer at handover
                <input
                  value={odometer}
                  onChange={(e) => setOdometer(e.target.value)}
                  inputMode="numeric"
                  className="ml-2 w-32 rounded border border-tcs-border px-2 py-1 text-sm"
                />
              </label>
              <Button size="sm" loading={busy} onClick={() => void tick()}>
                Record and confirm receipt
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAskOdometer(false)}>
                Cancel
              </Button>
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tcs-text-muted">
            <span>{ownerLabel}</span>
            {assignee && <span>· {assignee}</span>}
            {item.is_automatic && item.scheduled_for && !done && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> Scheduled {fmtDate(item.scheduled_for)}
              </span>
            )}
            {!item.is_automatic && item.due_date && !done && (
              <span>· Due {fmtDate(item.due_date)}</span>
            )}
            {done && item.completed_by_eid && (
              <span>
                · Done by {names.get(item.completed_by_eid) ?? item.completed_by_eid}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {isHandoff && onOpenHandoff && !done && (
            <Button size="sm" onClick={() => onOpenHandoff(item)}>
              Open assignment screen
            </Button>
          )}
          {item.item_key === 'app_access_removed' && !done && canAct && (
            <Button size="sm" variant="secondary" loading={busy} onClick={() => void revokeNow()}>
              Revoke now
            </Button>
          )}
          {!item.is_automatic && !done && canAct && (
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeftRight className="h-3.5 w-3.5" />}
              onClick={() => setReassigning(true)}
            >
              Reassign
            </Button>
          )}
        </div>
      </div>

      {reassigning && (
        <ReassignModal
          item={item}
          onClose={() => setReassigning(false)}
          onAssigned={() => {
            setReassigning(false);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ⛔ REASSIGNING AN ITEM DOES NOT GRANT ACCESS TO THE RECORD. The routing entity
// is the item, so the person who receives it sees that item and the screens it
// needs — no reason, no documents, no pay figures, no other owner's items.
// ---------------------------------------------------------------------------
function ReassignModal({
  item,
  onClose,
  onAssigned,
}: {
  item: ChecklistItem;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const toast = useToast();
  const { people, loading } = useEmployeePicker();
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!to) return;
    setBusy(true);
    try {
      await assignItem(item.item_id, to, note.trim() || null, keep);
      toast.success('Assigned', {
        description: keep
          ? 'It stays in your Waiting on others until they tick it.'
          : 'It has left your list.',
      });
      onAssigned();
    } catch (err) {
      toast.error("Couldn't assign that", {
        description: errorMessage(err, 'Try again.'),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Reassign ${item.label}`}
        className="w-full max-w-lg rounded-lg bg-tcs-surface p-5 shadow-lg"
      >
        <h2 className="text-base font-semibold text-tcs-text">Reassign: {item.label}</h2>
        <p className="mt-1 text-xs text-tcs-text-secondary">
          Currently {OWNER_LABELS[item.owner_position_key] ?? item.owner_position_key} ·{' '}
          {item.separation_id}
        </p>

        <label className="mt-4 block text-sm text-tcs-text-secondary">
          Assign to
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full rounded border border-tcs-border px-2 py-1.5 text-sm"
          >
            <option value="">{loading ? 'Loading…' : 'Pick an active employee…'}</option>
            {people.map((p) => (
              <option key={p.employee_id} value={p.employee_id}>
                {p.display_name ?? p.employee_id}
                {p.employee_title ? ` — ${p.employee_title}` : ''}
                {p.work_state ? ` · ${p.work_state}` : ''}
              </option>
            ))}
          </select>
        </label>
        <p className="mt-1 text-xs text-tcs-text-muted">
          Only active employees appear here.
        </p>

        <label className="mt-3 block text-sm text-tcs-text-secondary">
          Note — optional
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="You're the closest to Denver — can you take the laptop and truck handoff?"
            className="mt-1 w-full rounded border border-tcs-border px-2 py-1.5 text-sm"
          />
        </label>

        <label className="mt-3 flex items-start gap-2 text-sm text-tcs-text-secondary">
          <input
            type="checkbox"
            checked={keep}
            onChange={(e) => setKeep(e.target.checked)}
            className="mt-1"
          />
          <span>
            Keep this on my list too
            <span className="block text-xs text-tcs-text-muted">
              Shows in your Waiting on others until they tick it. Off means it leaves your
              list entirely.
            </span>
          </span>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!to} onClick={() => void submit()}>
            Assign
          </Button>
        </div>
      </div>
    </div>
  );
}
